import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button, Dialog, DialogContent, IconButton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ConfirmModal, IOrganisationType, SortOrder, StatusCode, Table, useQueryParamsContext } from '../../../shared';
import { useDeleteOrganisationType, useOrganisationTypeList } from '../../hooks/organisation-type.hook';
import { Edit } from '@mui/icons-material';

export function OrganisationTypesTable() {
    const { t } = useTranslation();
    const {
        queryParams: { page, pageSize, sort },
        setPage,
        setPageSize,
        setSort,
    } = useQueryParamsContext('organisation-types', {
        sort: [{ field: 'name', sort: 'asc' }],
    });
    const [errorDialog, setErrorDialog] = useState({ show: false, message: '' });

    const { isLoading, data: organisationTypes } = useOrganisationTypeList({
        page,
        pageSize,
        sortBy: [sort[0]?.field],
        sortOrder: sort[0]?.sort?.toUpperCase() as SortOrder,
    });

    const { mutateAsync: deleteOrganisationType } = useDeleteOrganisationType();

    const onDelete = async (id: string) => {
        try {
            await deleteOrganisationType({
                id,
                messages: {
                    onDeleteSuccessMessage: t('deleteOrganisationTypeSuccess'),
                    onDeleteErrorMessage: t('deleteOrganisationTypeError'),
                },
            });
        } catch (err) {
            if ((err as AxiosError)?.response?.status === StatusCode.CONFLICT) {
                setErrorDialog({ show: true, message: t('organisationTypeDeleteErr') });
            }
        }
    };

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('nameLabel'), minWidth: 150, flex: 1 },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id } }: GridCellParams<IOrganisationType>) => (
                <>
                    <IconButton component={Link} to={`/admin/organisation-types/edit/${id}`}>
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() => id && onDelete(id)}
                        button={
                            <IconButton aria-label="delete">
                                <DeleteOutlineIcon />
                            </IconButton>
                        }
                        title={t('typeDeleteWarningTitle')}
                        text={t('typeDeleteWarningText')}
                    />
                </>
            ),
        },
    ];

    return (
        <>
            <Table
                rows={organisationTypes?.data || []}
                columns={columns}
                rowCount={organisationTypes?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isLoading}
                sortModel={sort}
                onSortModelChange={setSort}
            />
            <Dialog
                open={errorDialog.show}
                onClose={() => setErrorDialog({ show: false, message: '' })}
                data-testid="error-dialog"
            >
                <DialogContent>{errorDialog.message}</DialogContent>
                <Button onClick={() => setErrorDialog({ show: false, message: '' })} color="primary" autoFocus>
                    {t('ok')}
                </Button>
            </Dialog>
        </>
    );
}
